import { useState } from 'react'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Play, Pause, Ban, CalendarClock } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { apiError } from '@/lib/api-client'
import { currentLocale, i18n } from '@/lib/i18n'
import { useMyPermissions } from '@/hooks/use-settings'
import {
  useChiTietChienDichNhanTin,
  useNguoiNhan,
  useDieuKhienChienDichNhanTin,
  useDieuKhienTaiKhoanNhanTin,
  tienDo,
  NHAN_TRANG_THAI,
  type TaiKhoanChienDich,
  type NguoiNhan,
} from '@/hooks/use-zalo-broadcast-campaigns'

const NHAN_TK: Record<string, string> = {
  get active() { return i18n.t('marketing:broadcastDetail.accountStatus.active') },
  get paused() { return i18n.t('marketing:broadcastDetail.accountStatus.paused') },
  get done() { return i18n.t('marketing:broadcastDetail.accountStatus.done') },
  get blocked() { return i18n.t('marketing:broadcastDetail.accountStatus.blocked') },
}
const NHAN_NGUOI: Record<string, string> = {
  get pending() { return i18n.t('marketing:broadcastDetail.recipientStatus.pending') },
  get sent() { return i18n.t('marketing:broadcastDetail.recipientStatus.sent') },
  get failed() { return i18n.t('marketing:broadcastDetail.recipientStatus.failed') },
  get skipped() { return i18n.t('marketing:broadcastDetail.recipientStatus.skipped') },
}

export function BroadcastCampaignDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('marketing')
  const { data: c, isLoading } = useChiTietChienDichNhanTin(id)
  const [loc, setLoc] = useState<string | undefined>()
  const { data: nguoi } = useNguoiNhan(id, { status: loc, take: 200 })
  const dieuKhien = useDieuKhienChienDichNhanTin(id!)
  const dieuKhienTk = useDieuKhienTaiKhoanNhanTin(id!)
  const [hen, setHen] = useState('')
  // Hẹn giờ / Chạy / Chạy tiếp / Tạm dừng / Huỷ và nút từng tài khoản: BE đòi broadcast_campaigns.run → thiếu thì ẩn (BRD §5.3.1).
  const { data: perms } = useMyPermissions()
  const coQuyenChay = !!perms?.has('broadcast_campaigns.run')

  if (isLoading || !c) return <div className="p-6 text-muted-foreground">{t('broadcastDetail.loading')}</div>

  const thongKe = new Map(c.thongKeTaiKhoan.map((x) => [x.channelAccountId, x]))

  async function chay(hanhDong: 'start' | 'pause' | 'resume' | 'cancel', scheduledAt?: string) {
    try {
      await dieuKhien.mutateAsync({ hanhDong, scheduledAt })
      toast.success(
        hanhDong === 'start' ? (scheduledAt ? t('broadcastDetail.scheduledToast') : t('broadcastDetail.startedToast'))
          : hanhDong === 'resume' ? t('broadcastDetail.resumedToast')
            : hanhDong === 'pause' ? t('broadcastDetail.pausedToast') : t('broadcastDetail.cancelledToast'),
      )
    } catch (e) { toast.error(apiError(e)) }
  }

  const cotTaiKhoan: Column<TaiKhoanChienDich>[] = [
    {
      key: 'tk', header: t('broadcastDetail.columns.account'),
      cell: (a) => (
        <div>
          <div className="font-medium">{a.channelAccount?.displayName ?? a.channelAccountId}</div>
          {a.stoppedReason && <div className="text-xs text-amber-600">{a.stoppedReason}</div>}
        </div>
      ),
    },
    {
      key: 'status', header: t('broadcastDetail.columns.status'),
      cell: (a) => (
        <Badge variant={a.status === 'blocked' ? 'destructive' : a.status === 'active' ? 'default' : 'secondary'}>
          {NHAN_TK[a.status] ?? a.status}
        </Badge>
      ),
    },
    {
      key: 'homNay', header: t('broadcastDetail.columns.today'), align: 'right',
      cell: (a) => <span className="tabular-nums">{(thongKe.get(a.channelAccountId)?.guiHomNay ?? 0).toLocaleString(currentLocale())}</span>,
    },
    {
      key: 'gui', header: t('broadcastDetail.columns.sentPending'), align: 'right',
      cell: (a) => {
        const tk = thongKe.get(a.channelAccountId)
        return <span className="tabular-nums">{(tk?.daGui ?? 0).toLocaleString(currentLocale())} / {(tk?.choGui ?? 0).toLocaleString(currentLocale())}</span>
      },
    },
    {
      key: 'ke', header: t('broadcastDetail.columns.nextMessage'),
      cell: (a) => a.nextSendAt
        ? <span className="tabular-nums">{dayjs(a.nextSendAt).format('HH:mm:ss DD/MM')}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    ...(coQuyenChay ? [{
      key: 'act', header: '', align: 'right' as const,
      cell: (a: TaiKhoanChienDich) => (
        <Button
          size="sm" variant="outline" disabled={a.status === 'done'}
          onClick={async () => {
            try {
              await dieuKhienTk.mutateAsync({
                accountId: a.channelAccountId,
                hanhDong: a.status === 'active' ? 'pause' : 'resume',
              })
            } catch (e) { toast.error(apiError(e)) }
          }}
        >
          {a.status === 'active' ? t('broadcastDetail.pauseAccount') : t('broadcastDetail.resumeAccount')}
        </Button>
      ),
    }] : []),
  ]

  const cotNguoi: Column<NguoiNhan>[] = [
    { key: 'ten', header: t('broadcastDetail.columns.friend'), cell: (k) => k.displayName || '—' },
    { key: 'sdt', header: t('broadcastDetail.columns.phone'), cell: (k) => <span className="tabular-nums">{k.phone ?? '—'}</span> },
    {
      key: 'st', header: t('broadcastDetail.columns.status'),
      cell: (k) => (
        <div>
          <Badge variant={k.status === 'sent' ? 'default' : k.status === 'failed' ? 'destructive' : 'secondary'}>
            {NHAN_NGUOI[k.status] ?? k.status}
          </Badge>
          {k.errorMessage && <div className="mt-1 text-xs text-muted-foreground">{k.errorMessage}</div>}
        </div>
      ),
    },
    {
      key: 'luc', header: t('broadcastDetail.columns.sentAt'), align: 'right',
      cell: (k) => k.sentAt ? <span className="tabular-nums">{dayjs(k.sentAt).format('HH:mm DD/MM')}</span> : '—',
    },
  ]

  const pct = tienDo(c)
  const choChay = c.status === 'draft' || c.status === 'paused' || c.status === 'scheduled'

  return (
    <div className="space-y-6">
      <PageHeader
        title={c.name}
        description={t('broadcastDetail.pageDescription', {
          status: NHAN_TRANG_THAI[c.status], total: c.totalCount.toLocaleString(currentLocale()),
          accounts: c.accounts.length, dailyLimit: c.dailyLimit, dailyTotalLimit: c.dailyTotalLimit.toLocaleString(currentLocale()),
          start: c.sendWindowStart, end: c.sendWindowEnd,
        }) + (c.scheduledAt && c.status === 'scheduled' ? ` · ${t('broadcastDetail.scheduledInline', { time: dayjs(c.scheduledAt).format('HH:mm DD/MM') })}` : '')}
        actions={
          coQuyenChay && (
            <div className="flex flex-wrap items-center gap-2">
              {c.status === 'draft' && (
                <>
                  <Input type="datetime-local" className="w-52" value={hen} onChange={(e) => setHen(e.target.value)} />
                  <Button variant="outline" disabled={!hen}
                    onClick={() => chay('start', new Date(hen).toISOString())}>
                    <CalendarClock className="h-4 w-4" /> {t('broadcastDetail.schedule')}
                  </Button>
                </>
              )}
              {choChay && c.status !== 'paused' && (
                <Button onClick={() => chay('start')}><Play className="h-4 w-4" /> {t('broadcastDetail.runNow')}</Button>
              )}
              {c.status === 'paused' && (
                <Button onClick={() => chay('resume')}><Play className="h-4 w-4" /> {t('broadcastDetail.resume')}</Button>
              )}
              {(c.status === 'running' || c.status === 'scheduled') && (
                <Button variant="outline" onClick={() => chay('pause')}><Pause className="h-4 w-4" /> {t('broadcastDetail.pause')}</Button>
              )}
              {!['cancelled', 'completed'].includes(c.status) && (
                <Button variant="outline" onClick={() => chay('cancel')}><Ban className="h-4 w-4" /> {t('broadcastDetail.cancel')}</Button>
              )}
            </div>
          )
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {[
          [t('broadcastDetail.stats.sent'), c.sentCount],
          [t('broadcastDetail.stats.sentToday'), c.guiHomNay],
          [t('broadcastDetail.stats.skipped'), c.skippedCount],
          [t('broadcastDetail.stats.failed'), c.failedCount],
        ].map(([nhan, v]) => (
          <div key={nhan as string} className="rounded-lg border p-4">
            <div className="text-sm text-muted-foreground">{nhan}</div>
            <div className="text-2xl font-semibold tabular-nums">{(v as number).toLocaleString(currentLocale())}</div>
          </div>
        ))}
        <div className="rounded-lg border p-4">
          <div className="text-sm text-muted-foreground">{t('broadcastDetail.stats.progress')}</div>
          <div className="text-2xl font-semibold tabular-nums">{c.totalCount > 0 ? `${(pct * 100).toFixed(0)}%` : '—'}</div>
        </div>
      </div>

      <Tabs defaultValue="taikhoan">
        <TabsList>
          <TabsTrigger value="taikhoan">{t('broadcastDetail.tabs.accounts', { count: c.accounts.length })}</TabsTrigger>
          <TabsTrigger value="nguoi">{t('broadcastDetail.tabs.recipients')}</TabsTrigger>
          <TabsTrigger value="noidung">{t('broadcastDetail.tabs.content', { count: c.messages.length })}</TabsTrigger>
        </TabsList>

        <TabsContent value="taikhoan">
          <DataTable columns={cotTaiKhoan} rows={c.accounts} rowKey={(a) => a.id}
            emptyTitle={coQuyenChay ? t('broadcastDetail.noAccountsCanRun') : t('broadcastDetail.noAccounts')} />
        </TabsContent>

        <TabsContent value="nguoi" className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={!loc ? 'default' : 'outline'} onClick={() => setLoc(undefined)}>{t('broadcastDetail.all')}</Button>
            {Object.entries(NHAN_NGUOI).map(([v, nhan]) => (
              <Button key={v} size="sm" variant={loc === v ? 'default' : 'outline'} onClick={() => setLoc(v)}>{nhan}</Button>
            ))}
          </div>
          <DataTable columns={cotNguoi} rows={nguoi?.rows ?? []} rowKey={(k) => k.id}
            emptyTitle={t('broadcastDetail.noRecipientsInStatus')} />
        </TabsContent>

        <TabsContent value="noidung" className="space-y-3">
          {c.messages.map((m, i) => (
            <div key={i} className="space-y-2 rounded-lg border p-3 text-sm">
              <div className="text-xs text-muted-foreground">{t('broadcastDetail.variantN', { n: i + 1 })}</div>
              {m.imageUrl && <img src={m.imageUrl} alt="" className="h-20 w-20 rounded border object-cover" />}
              {m.text && <div className="whitespace-pre-wrap">{m.text}</div>}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            {c.optOutFooter ? t('broadcastDetail.hasOptOutFooter') : t('broadcastDetail.noOptOutFooter')}
          </p>
        </TabsContent>
      </Tabs>
    </div>
  )
}
